import React, { useState, useCallback, useRef } from 'react';

const TABS = ['Geral', 'Hero', 'Seções', 'Rodapé', 'JSON'];

/* ─── helpers ────────────────────────────────────────────────────────────── */
function Field({ label, children }) {
  return (
    <div className="jb-ep__field">
      <label>{label}</label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = 'text' }) {
  return (
    <input
      type={type}
      value={value ?? ''}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function TextArea({ value, onChange, placeholder }) {
  return (
    <textarea
      value={value ?? ''}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <Field label={label}>
      <div className="jb-ep__color-row">
        <input type="color" value={value ?? '#000000'} onChange={(e) => onChange(e.target.value)} />
        <input
          type="text"
          value={value ?? ''}
          placeholder="#e8272a"
          onChange={(e) => { if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) onChange(e.target.value); }}
        />
      </div>
    </Field>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <div className="jb-ep__toggle-row">
      <span>{label}</span>
      <label className="jb-ep__toggle">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span className="jb-ep__toggle-slider" />
      </label>
    </div>
  );
}

/* ─── Accordion ──────────────────────────────────────────────────────────── */
function AccordionGroup({ children, defaultOpen = -1 }) {
  const [openIndex, setOpenIndex] = useState(defaultOpen);
  return (
    <div>
      {React.Children.map(children, (child, i) =>
        React.cloneElement(child, {
          isOpen: openIndex === i,
          onToggle: () => setOpenIndex(openIndex === i ? -1 : i),
        })
      )}
    </div>
  );
}

function Accordion({ title, children, isOpen, onToggle }) {
  return (
    <div className="jb-ep__accordion">
      <button className="jb-ep__accordion-header" onClick={onToggle}>
        <span>{title}</span>
        <span
          className="jb-ep__accordion-arrow"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >▾</span>
      </button>
      {isOpen && <div className="jb-ep__accordion-body">{children}</div>}
    </div>
  );
}

/* ─── Image upload ───────────────────────────────────────────────────────── */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ImageField({ label, value, onChange }) {
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onChange(await fileToBase64(file));
  };
  return (
    <Field label={label}>
      {value && (
        <img src={value} alt="preview"
          style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 3, marginBottom: 6, display: 'block' }}
        />
      )}
      <input type="file" accept="image/*" onChange={handleFile}
        style={{ display: 'block', fontSize: 11, color: '#aaa', marginBottom: 6, width: '100%' }}
      />
      <input
        type="text"
        value={value?.startsWith('data:') ? '' : (value ?? '')}
        placeholder="ou cole uma URL..."
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

/* ─── Sub-editors ────────────────────────────────────────────────────────── */
function ReviewEditor({ items, onChange }) {
  const upd = (i, f, v) => onChange(items.map((r, idx) => idx === i ? { ...r, [f]: v } : r));
  const rem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { name: 'Novo avaliador', time: 'agora', text: 'Ótimo serviço!', color: '#e8272a' }]);
  return (
    <>
      {items.map((r, i) => (
        <div className="jb-ep__review-item" key={i}>
          <input value={r.name} placeholder="Nome" onChange={(e) => upd(i, 'name', e.target.value)} />
          <input value={r.time} placeholder="Tempo" onChange={(e) => upd(i, 'time', e.target.value)} />
          <textarea value={r.text} placeholder="Texto" onChange={(e) => upd(i, 'text', e.target.value)} />
          <div className="jb-ep__review-row">
            <input type="color" value={r.color} onChange={(e) => upd(i, 'color', e.target.value)} />
            <span>cor do avatar</span>
            <button className="jb-ep__review-del" onClick={() => rem(i)}>✕</button>
          </div>
        </div>
      ))}
      <button className="jb-ep__btn-add" onClick={add}>+ Adicionar avaliação</button>
    </>
  );
}

function StepEditor({ steps, onChange }) {
  const upd = (i, f, v) => onChange(steps.map((s, idx) => idx === i ? { ...s, [f]: v } : s));
  return (
    <>
      {steps.map((s, i) => (
        <div className="jb-ep__review-item" key={i}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 5 }}>
            <input value={s.icon ?? ''} style={{ width: 44 }} onChange={(e) => upd(i, 'icon', e.target.value)} placeholder="emoji" />
            <input value={s.title} style={{ flex: 1 }} onChange={(e) => upd(i, 'title', e.target.value)} placeholder="Título" />
          </div>
          <textarea value={s.description} onChange={(e) => upd(i, 'description', e.target.value)} placeholder="Descrição" style={{ marginBottom: 6 }} />
          <ImageField
            label="Imagem personalizada (substitui o emoji)"
            value={s.image ?? ''}
            onChange={(v) => upd(i, 'image', v)}
          />
        </div>
      ))}
    </>
  );
}

function LogosEditor({ items, onChange }) {
  const upd = (i, f, v) => onChange(items.map((item, idx) => idx === i ? { ...item, [f]: v } : item));
  const rem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { name: 'Novo logo', url: '' }]);
  return (
    <>
      {items.map((item, i) => (
        <div className="jb-ep__review-item" key={i}>
          <input value={item.name} placeholder="Nome" onChange={(e) => upd(i, 'name', e.target.value)} style={{ marginBottom: 6 }} />
          <ImageField label="Logo" value={item.url} onChange={(v) => upd(i, 'url', v)} />
          <div className="jb-ep__review-row" style={{ marginTop: 4 }}>
            <button className="jb-ep__review-del" onClick={() => rem(i)}>✕ remover</button>
          </div>
        </div>
      ))}
      <button className="jb-ep__btn-add" onClick={add}>+ Adicionar logo</button>
    </>
  );
}

function PortfolioEditor({ items, onChange }) {
  const upd = (i, f, v) => onChange(items.map((item, idx) => idx === i ? { ...item, [f]: v } : item));
  const rem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { name: 'Novo parceiro', img: '' }]);
  return (
    <>
      {items.map((item, i) => (
        <div className="jb-ep__review-item" key={i}>
          <input value={item.name} placeholder="Nome" onChange={(e) => upd(i, 'name', e.target.value)} style={{ marginBottom: 6 }} />
          <ImageField label="Foto" value={item.img} onChange={(v) => upd(i, 'img', v)} />
          <div className="jb-ep__review-row" style={{ marginTop: 4 }}>
            <button className="jb-ep__review-del" onClick={() => rem(i)}>✕ remover</button>
          </div>
        </div>
      ))}
      <button className="jb-ep__btn-add" onClick={add}>+ Adicionar parceiro</button>
    </>
  );
}

function TestimonialsEditor({ items, onChange }) {
  const upd = (i, f, v) => onChange(items.map((item, idx) => idx === i ? { ...item, [f]: v } : item));
  const rem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { name: 'Nome', role: 'Cargo / Clube', text: 'Depoimento aqui...', thumb: '' }]);
  return (
    <>
      {items.map((item, i) => (
        <div className="jb-ep__review-item" key={i}>
          <input value={item.name} placeholder="Nome" onChange={(e) => upd(i, 'name', e.target.value)} />
          <input value={item.role ?? ''} placeholder="Cargo / Clube (opcional)" onChange={(e) => upd(i, 'role', e.target.value)} style={{ marginTop: 5 }} />
          <textarea value={item.text} placeholder="Depoimento" onChange={(e) => upd(i, 'text', e.target.value)} style={{ marginTop: 5 }} />
          <ImageField label="Foto" value={item.thumb} onChange={(v) => upd(i, 'thumb', v)} />
          <div className="jb-ep__review-row" style={{ marginTop: 4 }}>
            <button className="jb-ep__review-del" onClick={() => rem(i)}>✕ remover</button>
          </div>
        </div>
      ))}
      <button className="jb-ep__btn-add" onClick={add}>+ Adicionar depoimento</button>
    </>
  );
}

/* ─── Tabs ───────────────────────────────────────────────────────────────── */
function TabGeral({ config, update }) {
  return (
    <div className="jb-ep__body">
      <AccordionGroup defaultOpen={0}>
        <Accordion title="Identidade">
          <Field label="Nome da Marca">
            <TextInput value={config.brand} onChange={(v) => update({ brand: v })} />
          </Field>
          <ColorField label="Cor Principal" value={config.primaryColor} onChange={(v) => update({ primaryColor: v })} />
        </Accordion>
        <Accordion title="Navegação">
          <ImageField label="Logo do Header" value={config.nav.logoImage} onChange={(v) => update({ nav: { logoImage: v } })} />
          <Field label='Link "Iniciar Projeto"'>
            <TextInput type="url" value={config.nav.linkStartProject} onChange={(v) => update({ nav: { linkStartProject: v } })} />
          </Field>
          <Field label='Link "Sobre Nós"'>
            <TextInput type="url" value={config.nav.linkAbout} onChange={(v) => update({ nav: { linkAbout: v } })} />
          </Field>
          <Field label='Link "Encontre seu Esporte"'>
            <TextInput type="url" value={config.nav.linkSport} onChange={(v) => update({ nav: { linkSport: v } })} />
          </Field>
          <Field label='Link "Loja"'>
            <TextInput type="url" value={config.nav.linkShop} onChange={(v) => update({ nav: { linkShop: v } })} />
          </Field>
        </Accordion>
      </AccordionGroup>
    </div>
  );
}

function TabHero({ config, update }) {
  return (
    <div className="jb-ep__body">
      <AccordionGroup defaultOpen={0}>
        <Accordion title="Títulos">
          <Field label="Título Linha 1">
            <TextInput value={config.hero.titleLine1} onChange={(v) => update({ hero: { titleLine1: v } })} />
          </Field>
          <Field label="Título Linha 2">
            <TextInput value={config.hero.titleLine2} onChange={(v) => update({ hero: { titleLine2: v } })} />
          </Field>
          <Field label="Tamanho Fonte (px)">
            <TextInput value={String(config.hero.fontSize)} onChange={(v) => update({ hero: { fontSize: Number(v) || 86 } })} />
          </Field>
          <Field label="Subtítulo">
            <TextArea value={config.hero.subtitle} onChange={(v) => update({ hero: { subtitle: v } })} />
          </Field>
        </Accordion>
        <Accordion title="Botão CTA">
          <Field label="Texto do botão">
            <TextInput value={config.hero.ctaText} onChange={(v) => update({ hero: { ctaText: v } })} />
          </Field>
          <Field label="Link do botão">
            <TextInput type="url" value={config.hero.ctaLink} onChange={(v) => update({ hero: { ctaLink: v } })} />
          </Field>
        </Accordion>
        <Accordion title="Imagem de Fundo">
          <ImageField label="Imagem" value={config.hero.bgImage} onChange={(v) => update({ hero: { bgImage: v } })} />
          <ColorField label="Cor de fundo" value={config.hero.bgFallback} onChange={(v) => update({ hero: { bgFallback: v } })} />
        </Accordion>
      </AccordionGroup>
    </div>
  );
}

function TabSecoes({ config, update }) {
  return (
    <div className="jb-ep__body">
      <AccordionGroup>
        <Accordion title="Seção Principal">
          <Toggle label="Exibir" checked={config.boring.visible} onChange={(v) => update({ boring: { visible: v } })} />
          <Field label="Título">
            <TextArea value={config.boring.title} onChange={(v) => update({ boring: { title: v } })} />
          </Field>
          <Field label="Subtítulo">
            <TextArea value={config.boring.subtitle} onChange={(v) => update({ boring: { subtitle: v } })} />
          </Field>
        </Accordion>
        <Accordion title="Portfólio">
          <Toggle label="Exibir" checked={config.portfolio.visible} onChange={(v) => update({ portfolio: { visible: v } })} />
          <Field label="Título">
            <TextInput value={config.portfolio.title} onChange={(v) => update({ portfolio: { title: v } })} />
          </Field>
          <PortfolioEditor items={config.portfolio.items} onChange={(items) => update({ portfolio: { items } })} />
        </Accordion>
        <Accordion title="Logos / Marquee">
          <LogosEditor items={config.logos?.items ?? []} onChange={(items) => update({ logos: { items } })} />
        </Accordion>
        <Accordion title="Avaliações">
          <Toggle label="Exibir" checked={config.reviews.visible} onChange={(v) => update({ reviews: { visible: v } })} />
          <Field label="Título">
            <TextArea value={config.reviews.title} onChange={(v) => update({ reviews: { title: v } })} />
          </Field>
          <Field label="Subtítulo">
            <TextArea value={config.reviews.subtitle} onChange={(v) => update({ reviews: { subtitle: v } })} />
          </Field>
          <Field label="Nota Google">
            <TextInput value={config.reviews.googleScore} onChange={(v) => update({ reviews: { googleScore: v } })} />
          </Field>
          <Field label="Qtd. avaliações">
            <TextInput value={config.reviews.reviewCount} onChange={(v) => update({ reviews: { reviewCount: v } })} />
          </Field>
          <ReviewEditor items={config.reviews.items} onChange={(items) => update({ reviews: { items } })} />
        </Accordion>
        <Accordion title="Depoimentos">
          <Toggle label="Exibir" checked={config.testimonials?.visible ?? true} onChange={(v) => update({ testimonials: { visible: v } })} />
          <Field label="Título da seção">
            <TextInput value={config.testimonials?.title ?? ''} onChange={(v) => update({ testimonials: { title: v } })} />
          </Field>
          <TestimonialsEditor items={config.testimonials?.items ?? []} onChange={(items) => update({ testimonials: { items } })} />
        </Accordion>
        <Accordion title="Como Funciona">
          <Toggle label="Exibir" checked={config.howItWorks.visible} onChange={(v) => update({ howItWorks: { visible: v } })} />
          <Field label="Título">
            <TextInput value={config.howItWorks.title} onChange={(v) => update({ howItWorks: { title: v } })} />
          </Field>
          <StepEditor steps={config.howItWorks.steps} onChange={(steps) => update({ howItWorks: { steps } })} />
        </Accordion>
        <Accordion title="Categorias de Designs">
          <Toggle label="Exibir" checked={config.categories?.visible ?? true} onChange={(v) => update({ categories: { visible: v } })} />
          <Field label="Título da seção">
            <TextInput value={config.categories?.title ?? ''} onChange={(v) => update({ categories: { title: v } })} placeholder="NOSSOS DESIGNS" />
          </Field>
          <Field label="Subtítulo">
            <TextInput value={config.categories?.subtitle ?? ''} onChange={(v) => update({ categories: { subtitle: v } })} placeholder="Explore nossa coleção" />
          </Field>
          <div style={{ fontSize: 10, color: '#666', margin: '6px 0 10px', lineHeight: 1.5 }}>
            Thumbnails por categoria (carregadas automaticamente do sistema):
          </div>
          <CategoryThumbEditor
            thumbnails={config.categories?.thumbnails ?? {}}
            onChange={(thumbnails) => update({ categories: { thumbnails } })}
          />
        </Accordion>

        <Accordion title="Localização / Mapa">
          <Toggle label="Exibir" checked={config.location?.visible ?? false} onChange={(v) => update({ location: { visible: v } })} />
          <Field label="Título da seção">
            <TextInput value={config.location?.title ?? ''} onChange={(v) => update({ location: { title: v } })} placeholder="Nossa Localização" />
          </Field>
          <Field label="Subtítulo">
            <TextInput value={config.location?.subtitle ?? ''} onChange={(v) => update({ location: { subtitle: v } })} placeholder="Venha nos visitar" />
          </Field>
          <Field label="URL de incorporação do Google Maps">
            <TextInput type="url" value={config.location?.embedUrl ?? ''} onChange={(v) => update({ location: { embedUrl: v } })} placeholder="https://www.google.com/maps/embed?pb=..." />
          </Field>
          <div style={{ fontSize: 10, color: '#666', marginTop: -8, marginBottom: 10, lineHeight: 1.5 }}>
            No Google Maps: clique em Compartilhar → Incorporar um mapa → copie o src do iframe
          </div>
          <Field label="Endereço (opcional)">
            <TextInput value={config.location?.address ?? ''} onChange={(v) => update({ location: { address: v } })} placeholder="Rua Exemplo, 123 – Cidade" />
          </Field>
          <Field label="Telefone (opcional)">
            <TextInput value={config.location?.phone ?? ''} onChange={(v) => update({ location: { phone: v } })} placeholder="(11) 99999-9999" />
          </Field>
          <Field label="Horário (opcional)">
            <TextInput value={config.location?.hours ?? ''} onChange={(v) => update({ location: { hours: v } })} placeholder="Seg–Sex: 9h–18h" />
          </Field>
        </Accordion>
      </AccordionGroup>
    </div>
  );
}

function FooterLinksEditor({ links, onChange }) {
  const upd = (i, f, v) => onChange(links.map((l, idx) => idx === i ? { ...l, [f]: v } : l));
  const rem = (i) => onChange(links.filter((_, idx) => idx !== i));
  const add = () => onChange([...links, { label: 'Novo link', href: 'https://' }]);

  return (
    <>
      {links.map((l, i) => (
        <div className="jb-ep__review-item" key={i}>
          <input
            value={l.label}
            placeholder="Texto do link"
            onChange={(e) => upd(i, 'label', e.target.value)}
            style={{ marginBottom: 5 }}
          />
          <input
            value={l.href}
            placeholder="https://..."
            onChange={(e) => upd(i, 'href', e.target.value)}
          />
          <div className="jb-ep__review-row" style={{ marginTop: 4 }}>
            <button className="jb-ep__review-del" onClick={() => rem(i)}>✕ remover</button>
          </div>
        </div>
      ))}
      <button className="jb-ep__btn-add" onClick={add}>+ Adicionar link</button>
    </>
  );
}

function TabRodapé({ config, update }) {
  return (
    <div className="jb-ep__body">
      <AccordionGroup defaultOpen={0}>
        <Accordion title="Textos">
          <Field label="Tagline">
            <TextArea value={config.footer.tagline} onChange={(v) => update({ footer: { tagline: v } })} />
          </Field>
          <Field label="Placeholder do e-mail">
            <TextInput value={config.footer.emailPlaceholder} onChange={(v) => update({ footer: { emailPlaceholder: v } })} />
          </Field>
          <Field label="Copyright">
            <TextInput value={config.footer.copyright} onChange={(v) => update({ footer: { copyright: v } })} />
          </Field>
        </Accordion>
        <Accordion title="Redes Sociais">
          <Field label="Instagram URL">
            <TextInput type="url" value={config.footer.socials.instagram} onChange={(v) => update({ footer: { socials: { instagram: v } } })} />
          </Field>
          <Field label="Facebook URL">
            <TextInput type="url" value={config.footer.socials.facebook} onChange={(v) => update({ footer: { socials: { facebook: v } } })} />
          </Field>
          <Field label="Twitter URL">
            <TextInput type="url" value={config.footer.socials.twitter} onChange={(v) => update({ footer: { socials: { twitter: v } } })} />
          </Field>
          <Field label="TikTok URL">
            <TextInput type="url" value={config.footer.socials.tiktok} onChange={(v) => update({ footer: { socials: { tiktok: v } } })} />
          </Field>
        </Accordion>
        <Accordion title="Links do Rodapé">
          <FooterLinksEditor
            links={config.footer.links ?? []}
            onChange={(links) => update({ footer: { links } })}
          />
        </Accordion>
      </AccordionGroup>
    </div>
  );
}

function TabJSON({ config, exportConfig, importConfig }) {
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [error, setError] = useState('');

  const handleImport = () => {
    const result = importConfig(importText);
    if (result.ok) { setShowImport(false); setImportText(''); setError(''); }
    else setError('JSON inválido: ' + result.error);
  };

  const handleExport = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([exportConfig()], { type: 'application/json' }));
    a.download = 'jerseybird-config.json';
    a.click();
  };

  return (
    <div className="jb-ep__body">
      <div className="jb-ep__divider">JSON atual</div>
      <pre className="jb-ep__json-out">{JSON.stringify(config, null, 2)}</pre>
      <button className="jb-ep__btn-secondary" style={{ marginTop: 10 }} onClick={() => setShowImport(s => !s)}>
        {showImport ? '✕ Cancelar' : '⬆ Importar JSON'}
      </button>
      {showImport && (
        <>
          <textarea className="jb-ep__json-import" placeholder="Cole o JSON aqui..." value={importText} onChange={(e) => setImportText(e.target.value)} />
          {error && <div style={{ color: '#f87171', fontSize: 11, marginTop: 4 }}>{error}</div>}
          <button className="jb-ep__btn-save" onClick={handleImport}>✓ CONFIRMAR IMPORTAÇÃO</button>
        </>
      )}
      <button className="jb-ep__btn-secondary" onClick={handleExport}>⬇ Exportar JSON</button>
    </div>
  );
}


/* ─── Category Thumbnails Editor ─────────────────────────────────────────── */
function CategoryThumbEditor({ thumbnails, onChange }) {
  const [cats, setCats] = React.useState([]);

  React.useEffect(() => {
    const loaded = window.categoriesLoaded ?? [];
    setCats(loaded);
    // Re-check when categories load later
    const interval = setInterval(() => {
      const c = window.categoriesLoaded ?? [];
      if (c.length !== cats.length) setCats(c);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  if (cats.length === 0) {
    return <div style={{ fontSize: 11, color: '#666', padding: '8px 0' }}>Nenhuma categoria carregada ainda. Abra a landing com um usuário logado.</div>;
  }

  return (
    <>
      {cats.map((cat, i) => {
        const key = String(cat.dbId ?? cat.id);
        return (
          <div key={i} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: '#aaa', marginBottom: 4 }}>{cat.name}</div>
            <ImageField
              label="Thumbnail"
              value={thumbnails[key] ?? ''}
              onChange={(v) => onChange({ ...thumbnails, [key]: v })}
            />
          </div>
        );
      })}
    </>
  );
}

/* ─── Main ───────────────────────────────────────────────────────────────── */
export default function EditorPanel({ config, updateConfig, exportConfig, importConfig, onReset }) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [saved, setSaved] = useState(false);

  const handleSave = useCallback(() => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }, []);

  const tabProps = { config, update: updateConfig };

  return (
    <>
      <div style={{ position: 'fixed', bottom: 22, right: 22, zIndex: 9999, display: 'flex', gap: 8 }}>
        <button id="edit-landing-iframe" className="jb-editor-btn" onClick={() => setOpen(s => !s)}>
          ⚙ EDITOR
        </button>
        <button id="close-landing-iframe" className="jb-editor-btn" style={{ background: '#333', boxShadow: 'none', marginRight: '110px' }}>
          ✕ FECHAR
        </button>
      </div>

      <div className={`jb-editor-panel ${open ? 'jb-editor-panel--open' : ''}`}>
        <div className="jb-ep__head">
          <h2>🎨 Editor de Página</h2>
          <button className="jb-ep__close" onClick={() => setOpen(false)}>✕</button>
        </div>

        <div className="jb-ep__tabs">
          {TABS.map((tab, i) => (
            <div key={tab} className={`jb-ep__tab ${activeTab === i ? 'jb-ep__tab--active' : ''}`} onClick={() => setActiveTab(i)}>
              {tab}
            </div>
          ))}
        </div>

        {activeTab === 0 && <TabGeral {...tabProps} />}
        {activeTab === 1 && <TabHero {...tabProps} />}
        {activeTab === 2 && <TabSecoes {...tabProps} />}
        {activeTab === 3 && <TabRodapé {...tabProps} />}
        {activeTab === 4 && <TabJSON config={config} exportConfig={exportConfig} importConfig={importConfig} />}

        {activeTab !== 4 && (
          <div style={{ padding: '0 16px' }}>
            <button className={`jb-ep__btn-save ${saved ? 'jb-ep__btn-save--success' : ''}`} onClick={handleSave}>
              {saved ? '✓ SALVO!' : '💾 SALVAR'}
            </button>
            <button className="jb-ep__btn-secondary" onClick={onReset}>↺ Restaurar padrão</button>
          </div>
        )}
      </div>
    </>
  );
}